/**
 * @fileoverview Zod v4-compatible resolver for react-hook-form
 * @description Wraps @hookform/resolvers/zod to handle Zod v4's error format
 * where errors use `.issues` instead of `.errors`. Also exports a submit-time
 * error handler that shows toast notifications for validation errors.
 */

import { toNestErrors, validateFieldsNatively } from '@hookform/resolvers'
import {
  type FieldError,
  type FieldErrors,
  type FieldValues,
  type ResolverOptions,
  type ResolverResult,
  appendErrors,
} from 'react-hook-form'
import { toast } from 'sonner'
import type { ZodType } from 'zod'

interface ZodIssue {
  path: (string | number)[]
  message: string
  code: string
  unionErrors?: Array<{ errors: ZodIssue[]; issues: ZodIssue[] }>
}

/**
 * Parses Zod error issues into react-hook-form field errors
 */
function parseZodIssues(
  issues: ZodIssue[],
  validateAllFieldCriteria: boolean,
): Record<string, FieldError> {
  const errors: Record<string, FieldError> = {}
  const queue = [...issues]

  while (queue.length > 0) {
    const issue = queue.shift()!
    const { code, message, path } = issue
    const fieldPath = path.join('.')

    if (!errors[fieldPath]) {
      if ('unionErrors' in issue && issue.unionErrors) {
        const firstUnionIssue =
          issue.unionErrors[0]?.issues?.[0] || issue.unionErrors[0]?.errors?.[0]
        if (firstUnionIssue) {
          errors[fieldPath] = {
            message: firstUnionIssue.message,
            type: firstUnionIssue.code,
          }
        } else {
          errors[fieldPath] = { message, type: code }
        }
      } else {
        errors[fieldPath] = { message, type: code }
      }
    }

    if ('unionErrors' in issue && issue.unionErrors) {
      for (const unionError of issue.unionErrors) {
        const unionIssues = unionError.issues || unionError.errors || []
        for (const e of unionIssues) {
          queue.push(e)
        }
      }
    }

    if (validateAllFieldCriteria) {
      const types = errors[fieldPath].types
      const messages = types && types[code]

      errors[fieldPath] = appendErrors(
        fieldPath,
        validateAllFieldCriteria,
        errors,
        code,
        messages
          ? ([] as string[]).concat(messages as string[], message)
          : message,
      ) as FieldError
    }
  }

  return errors
}

/**
 * Zod v4-compatible resolver for react-hook-form.
 * Handles both Zod v3 (.errors) and Zod v4 (.issues) error formats.
 */
export function safeZodResolver<T extends ZodType>(
  schema: T,
  schemaOptions?: Partial<{ async: boolean }>,
  resolverOptions: { mode?: 'sync' | 'async'; raw?: boolean } = {},
) {
  return async (
    values: FieldValues,
    _context: unknown,
    options: ResolverOptions<FieldValues>,
  ): Promise<ResolverResult<FieldValues>> => {
    try {
      const data = await schema[
        resolverOptions.mode === 'sync' ? 'parse' : 'parseAsync'
      ](values, schemaOptions)

      options.shouldUseNativeValidation && validateFieldsNatively({}, options)

      return {
        errors: {} as FieldErrors,
        values: resolverOptions.raw ? values : data,
      }
    } catch (error: unknown) {
      const zodError = error as { issues?: ZodIssue[]; errors?: ZodIssue[] }
      const issues = zodError?.issues || zodError?.errors

      if (Array.isArray(issues) && issues.length > 0) {
        return {
          values: {},
          errors: toNestErrors(
            parseZodIssues(
              issues,
              !options.shouldUseNativeValidation &&
                options.criteriaMode === 'all',
            ),
            options,
          ),
        }
      }

      throw error
    }
  }
}

/**
 * Submit-time error handler for react-hook-form.
 * Shows a toast with the first validation error message.
 * Pass as the second argument to form.handleSubmit().
 */
export function onFormValidationError(errors: Record<string, { message?: string }>) {
  const firstError = Object.values(errors)[0]
  const message = firstError?.message
  if (typeof message === 'string') {
    toast.error(message)
  }
}
